import { computed, ref, onMounted, watch } from 'vue';
import { useDebounceFn, useIntervalFn } from '@vueuse/core';
import NotificationsAPI from 'dashboard/api/notifications';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import { useEmitter } from 'dashboard/composables/emitter';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { BUS_EVENTS } from 'shared/constants/busEvents';

export const hierarchyGroupKey = group =>
  'assignee_id' in group
    ? `${group.inbox_id}:assignee:${group.assignee_id ?? 'none'}`
    : `${group.inbox_id}:${group.team_id ?? 'none'}`;

const REFRESH_INTERVAL_MS = 60000;
const REFRESH_DEBOUNCE_MS = 500;
const REFRESH_MAX_WAIT_MS = 2000;
export const INBOX_HIERARCHY_CONTEXT = 'inboxHierarchyActive';

export function useInboxHierarchy() {
  const { uiSettings, updateUISettings } = useUISettings();
  // The previous setting represented mutually exclusive views. Adopt the
  // combined default once, then preserve choices made in the new menu.
  const savedView = uiSettings.value.inbox_hierarchy_display_filter;
  const view = ref(
    ['unread', 'waiting', 'read', 'unread_waiting'].includes(savedView)
      ? savedView
      : 'unread_waiting'
  );
  const savedGrouping = uiSettings.value.inbox_hierarchy_group_by;
  const groupBy = ref(savedGrouping === 'assignee' ? 'assignee' : 'team');
  const groups = ref([]);
  const expandedInboxes = ref({});
  const expandedGroups = ref({});
  const pages = ref({});
  const cards = ref({});
  const error = ref(false);
  const now = ref(Date.now());
  const { run, isPending } = useAbortableRequest();

  const inboxes = computed(() => {
    const byInbox = new Map();
    groups.value.forEach(group => {
      if (!byInbox.has(group.inbox_id)) {
        byInbox.set(group.inbox_id, {
          id: group.inbox_id,
          name: group.inbox_name,
          count: 0,
          waiting_count: 0,
          oldest_waiting_since: null,
          groups: [],
        });
      }
      const inbox = byInbox.get(group.inbox_id);
      inbox.count += group.count;
      inbox.waiting_count += group.waiting_count;
      if (
        group.oldest_waiting_since &&
        (!inbox.oldest_waiting_since ||
          group.oldest_waiting_since < inbox.oldest_waiting_since)
      ) {
        inbox.oldest_waiting_since = group.oldest_waiting_since;
      }
      inbox.groups.push(group);
    });
    byInbox.forEach(inbox => {
      inbox.groups.sort((a, b) => {
        const column = groupBy.value === 'assignee' ? 'assignee' : 'team';
        return (
          Number(a[`${column}_id`] === null) -
            Number(b[`${column}_id`] === null) ||
          (a[`${column}_name`] || '').localeCompare(b[`${column}_name`] || '')
        );
      });
    });
    return Array.from(byInbox.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  });

  const refresh = async () => {
    error.value = false;
    try {
      const result = await run(async signal => {
        const filters = { view: view.value, group_by: groupBy.value };
        const groupColumn =
          filters.group_by === 'assignee' ? 'assignee_id' : 'team_id';
        const { data } = await NotificationsAPI.getConversations(filters, {
          signal,
        });
        const visibleGroups = data.groups.filter(
          group =>
            expandedInboxes.value[group.inbox_id] &&
            expandedGroups.value[hierarchyGroupKey(group)]
        );
        const loaded = await Promise.all(
          visibleGroups.map(async group => {
            const key = hierarchyGroupKey(group);
            const responses = await Promise.all(
              Array.from({ length: pages.value[key] || 1 }, (_, index) =>
                NotificationsAPI.getConversations(
                  {
                    ...filters,
                    inbox_id: group.inbox_id,
                    [groupColumn]: group[groupColumn] ?? 'none',
                    page: index + 1,
                  },
                  { signal }
                )
              )
            );
            const items = responses.flatMap(response => response.data.items);
            return [
              key,
              [...new Map(items.map(item => [item.id, item])).values()],
            ];
          })
        );
        return { groups: data.groups, cards: Object.fromEntries(loaded) };
      });
      if (!result) return;
      groups.value = result.groups;
      cards.value = result.cards;
    } catch {
      error.value = true;
    }
  };

  const scheduleRefresh = useDebounceFn(refresh, REFRESH_DEBOUNCE_MS, {
    maxWait: REFRESH_MAX_WAIT_MS,
  });
  const toggleInbox = id => {
    expandedInboxes.value[id] = !expandedInboxes.value[id];
    if (expandedInboxes.value[id]) refresh();
  };
  const toggleGroup = group => {
    const key = hierarchyGroupKey(group);
    expandedGroups.value[key] = !expandedGroups.value[key];
    if (expandedGroups.value[key]) refresh();
  };
  const loadMore = group => {
    const key = hierarchyGroupKey(group);
    pages.value[key] = (pages.value[key] || 1) + 1;
    refresh();
  };
  const collapseAll = () => {
    expandedInboxes.value = {};
    expandedGroups.value = {};
  };

  watch([view, groupBy], ([, grouping], [, previousGrouping]) => {
    cards.value = {};
    groups.value = [];
    pages.value = {};
    if (grouping !== previousGrouping) expandedGroups.value = {};
    updateUISettings({
      inbox_hierarchy_display_filter: view.value,
      inbox_hierarchy_group_by: groupBy.value,
    });
    refresh();
  });
  useEmitter(BUS_EVENTS.INBOX_HIERARCHY_UPDATED, scheduleRefresh);
  useEmitter(BUS_EVENTS.WEBSOCKET_RECONNECT, scheduleRefresh);
  useIntervalFn(() => {
    now.value = Date.now();
    refresh();
  }, REFRESH_INTERVAL_MS);
  onMounted(refresh);

  return {
    view,
    groupBy,
    inboxes,
    expandedInboxes,
    expandedGroups,
    cards,
    now,
    isPending,
    error,
    refresh,
    toggleInbox,
    toggleGroup,
    loadMore,
    collapseAll,
  };
}
